import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ShoppingBag, Store, ArrowLeft, Megaphone } from "lucide-react";
import { ProductCollection } from "@/components/storefront/ProductCollection";
import { StoreHero, StoreNav, StoreFooter, WhatsAppButton } from "@/components/storefront/StoreChrome";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";
const DEFAULT_ACCENT = "#10b981";

const StoreContentPage = ({ page }) => (
  <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="store-content-page">
    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-6">{page.title}</h2>
    <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 text-gray-700 whitespace-pre-line leading-relaxed">{page.content}</div>
  </article>
);

const StoreHome = ({ store, products, accent }) => {
  const featured = products.filter((p) => store.featured_product_ids?.includes(p.id));
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
      {featured.length > 0 && (
        <section data-testid="store-featured-section">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">Featured Picks</h2>
          <ProductCollection products={featured} layout="grid" accent={accent} />
        </section>
      )}
      <section>
        <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6" data-testid="store-products-heading">All Products ({products.length})</h2>
        {products.length > 0 ? (
          <ProductCollection products={products} layout={store.layout} accent={accent} />
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl">
            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No products available yet</p>
          </div>
        )}
      </section>
      {store.about_text && (
        <section className="rounded-3xl p-8 md:p-12 text-white" style={{ backgroundColor: accent }} data-testid="store-about-section">
          <h2 className="font-heading text-2xl font-bold mb-4">About {store.business_name}</h2>
          <p className="max-w-3xl leading-relaxed opacity-95 whitespace-pre-line">{store.about_text}</p>
        </section>
      )}
    </div>
  );
};

const StorePage = () => {
  const { storeSlug, pageSlug } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStore = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/store/${storeSlug}`);
        setStore(res.data.seller);
        setProducts(res.data.products || []);
      } catch (error) {
        setStore(null);
      }
      setLoading(false);
    };
    fetchStore();
  }, [storeSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="store-not-found">
        <div className="text-center">
          <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Store Not Found</h2>
          <p className="text-gray-500 mb-4">This store doesn't exist or is not approved yet.</p>
          <Link to="/" className="text-primary-600 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const accent = store.theme_color || DEFAULT_ACCENT;
  const page = pageSlug ? store.pages?.find((p) => p.slug === pageSlug) : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {store.announcement && (
        <div className="text-white text-sm py-2 px-4 text-center flex items-center justify-center gap-2" style={{ backgroundColor: accent }} data-testid="store-announcement-bar">
          <Megaphone className="w-4 h-4" />{store.announcement}
        </div>
      )}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/"><img src={LOGO_URL} alt="M Ventures" className="h-10 w-auto" /></Link>
          <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/products"))} className="text-gray-600 hover:text-primary-600 flex items-center gap-2" data-testid="store-back-to-deals-btn">
            <ArrowLeft className="w-4 h-4" />Back to Deals
          </button>
        </div>
      </header>
      <StoreHero store={store} accent={accent} productCount={products.length} />
      <StoreNav store={store} accent={accent} activePage={pageSlug} />
      <main className="flex-1">
        {pageSlug ? (
          page ? <StoreContentPage page={page} /> : <p className="text-center text-gray-500 py-16" data-testid="store-page-missing">Page not found.</p>
        ) : (
          <StoreHome store={store} products={products} accent={accent} />
        )}
      </main>
      <StoreFooter store={store} accent={accent} />
      <WhatsAppButton number={store.whatsapp} storeName={store.business_name} />
    </div>
  );
};

export default StorePage;
